/**
 * /table — Concierge Whisper · global verified restaurant discovery
 * and reservation orchestration surface.
 *
 * Founder ruling binding (5 Jun 2026):
 *   - Restaurants only · lc_archive never surfaces here.
 *   - Card click opens the rich detail drawer. The booking action
 *     lives only at the bottom of the drawer.
 *   - Same-tab redirect for verified direct destinations (platform /
 *     google_reserve / website / whatsapp). No embed in v1.
 *   - Concierge modal only when the resolver returns concierge OR
 *     when the member explicitly overrides via "have the concierge
 *     handle it instead" from the drawer.
 *   - Success toast fires ONLY after a real /concierge-request POST
 *     returns ok=true.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    searchTable,
    logTableEvent,
    logTableIntent,
    getVenueDetail,
    pingDishQuery,
    browseTable,
    parseTableQuery,
    searchNearbyTable,
    agentPlan,
} from "@/lib/table-api";
import { fireSelfServeIntent } from "@/lib/self-serve-intent";
import { MiraPlanView } from "@/components/table/MiraPlanView";
import { TableHeader } from "@/components/table/TableHeader";
import { TableFooter } from "@/components/table/TableFooter";
import TopCitiesGrid, { PICKS_TABLE } from "@/components/global/TopCitiesGrid";
import { TableHome } from "@/components/table/TableHome";
import { SearchField } from "@/components/table/SearchField";
import { RestaurantCard } from "@/components/table/RestaurantCard";
import PillarCard from "@/components/shared/PillarCard";

import MemberContextBand from "../components/MemberContextBand";
/**
 * Adapter — maps a /table venue shape to PillarCard's `venue` prop.
 *
 * /table venues come from a multi-source pipeline (curated DB · Google
 * Places · Agent plan) so the image source cascades across three
 * fields. We collapse it to `photo_url`. Address tail comes from
 * (neighborhood/city/country).
 */
function _tableVenueForPillar(v) {
    const photo =
        (typeof v?.image_url === "string" && v.image_url.trim()) ||
        (typeof v?.hero_image_url === "string" && v.hero_image_url.trim()) ||
        (Array.isArray(v?.photos) && v.photos.find((p) => typeof p === "string" && p.trim())) ||
        null;
    const addr =
        v?.neighborhood
            ? [v.neighborhood, v.city].filter(Boolean).join(", ")
            : [v?.city, v?.country].filter(Boolean).join(", ");
    return {
        name: v?.name,
        photo_url: photo,
        short_address: addr,
        address_line: v?.address_line || addr,
        city_name: v?.city,
        rating: v?.rating,
        user_ratings_total: v?.user_ratings_total,
    };
}
import { BrandConcierge } from "@/components/BrandConcierge";
import { RestaurantDrawer } from "@/components/table/RestaurantDrawer";
import { BookingSheet } from "@/components/table/BookingSheet";
import { FilterBar } from "@/components/table/FilterBar";
import { TableTabs, TAB_KEYS } from "@/components/table/TableTabs";
import { isEmbedded } from "@/lib/embed-context";
import { useConsoleAuth } from "@/lib/console-auth";
import { FilterHonestyBanner } from "@/components/table/FilterHonestyBanner";
import { BrowseFilterStrip } from "@/components/table/BrowseFilterStrip";
import { BrowseSearchBar } from "@/components/table/BrowseSearchBar";
import { NearMeToggle } from "@/components/table/NearMeToggle";
import { SortChips } from "@/components/table/SortChips";
import { DidYouMean } from "@/components/table/DidYouMean";
import { ConciergeNoticed } from "@/components/table/ConciergeNoticed";
import { rememberRecentSearch } from "@/components/table/RecentSearches";
import { envelopeToIntent } from "@/components/table/query-intent";
import { classifyRoute, isExternalHandoff } from "@/components/table/route-utils";

function useQueryParam(name) {
    return new URLSearchParams(window.location.search).get(name) || "";
}

// ─── Cross-query state-hygiene helpers · founder direction 10 Feb 2026 ──
// "search something then try do another one · no filters clear properly"
//
// `isFreshQueryInvocation` distinguishes a chip-toggle / sort-toggle /
// intent-toggle on the SAME query (which carries the relevant opt
// explicitly · see runSearch line ~360 chip handler and runBrowse's
// `handleBrowseFilterChange`) from a brand-new entry point (member
// types a query, clicks a tab, hits "Near me", clears city, etc).
// Both runSearch AND runBrowse use the same predicate so neither path
// can silently inherit chip / sort / intent state from a prior run.
//
// `FILTERS_EMPTY` must match the `useState` initialiser for the
// `filters` state below · a drift here means "default" and "reset"
// would produce structurally different empties.
function isFreshQueryInvocation(opts) {
    return (
        opts.filters === undefined
        && opts.sort === undefined
        && opts.intent === undefined
    );
}

const FILTERS_EMPTY = {
    cuisine: null, price_max: null, price_min: null,
    rating_min: null, route_in: null, open_now: null,
};

export default function TablePage() {
    const seed = useQueryParam("q");
    const tabSeed = useQueryParam("tab");
    const citySeed = useQueryParam("city");
    // Console-session gate · the Curate tab is a desk/concierge
    // tool. Member sessions get the standard 6-tab strip; the
    // console roles `admin` / `concierge_admin` ALSO see a 7th
    // [Curate ↗] navigation tab (founder direction 11 Feb 2026 PM).
    // Two-lock defence: even if a member URL-hacks `?tab=curate`,
    // `TableCurate.jsx` still bounces non-console sessions to
    // /mira/console/login.
    const consoleAuth = useConsoleAuth();
    const isConsoleSession = Boolean(
        consoleAuth?.user
        && (Array.isArray(consoleAuth.user.roles)
            ? (consoleAuth.user.roles.includes("admin")
               || consoleAuth.user.roles.includes("concierge_admin"))
            : false)
    );
    // view modes:
    //   "home"     · landing surface with search + examples
    //   "results"  · live search results
    //   "browse"   · category-tab archive grid (founder spec 8 Feb 2026)
    const initialView = seed ? "results" : (tabSeed && TAB_KEYS.includes(tabSeed) ? "browse" : "home");
    const [view, setView] = useState(initialView);
    const [q, setQ] = useState(seed);
    const [submitted, setSubmitted] = useState(seed);
    const [results, setResults] = useState([]);
    // Tab strip state (founder spec 8 Feb 2026)
    const [activeTab, setActiveTab] = useState(
        tabSeed && TAB_KEYS.includes(tabSeed) ? tabSeed : null
    );
    const [activeCity, setActiveCity] = useState(citySeed || null);
    // Founder filter spec 9 Feb 2026 · per-tab filter chip strip ·
    // generic engine · all state lives in the URL.
    const [activeFilters, setActiveFilters] = useState(() => {
        const sp = new URLSearchParams(window.location.search);
        const f = {};
        if (sp.get("city")) f.city = sp.get("city");
        if (sp.get("cuisine")) f.cuisine = sp.get("cuisine");
        if (sp.get("price_max")) f.price_max = sp.get("price_max");
        if (sp.get("stars")) {
            f.stars = sp.get("stars").split(",")
                .map(x => parseInt(x.trim(), 10))
                .filter(x => [1, 2, 3].includes(x));
        }
        if (sp.get("open_now")) f.open_now = true;
        if (sp.get("authority")) f.authority = sp.get("authority");
        if (sp.get("verification")) f.verification = sp.get("verification");
        return f;
    });
    const [cityCounts, setCityCounts] = useState(null);
    // BC-017 · the filtered total from /browse · NOT the unfiltered
    // /browse-counts total. The "REFINE · N VENUES" copy reads this
    // (via BrowseFilterStrip's `filteredTotal` prop) so the count
    // tells the same truth as the cards · founder catch 10 Feb 2026.
    const [browseTotal, setBrowseTotal] = useState(null);
    // BC-011 · honest coverage_gap envelope from /api/table/browse
    // (currently only emitted for tab=michelin when overlay has no
    // verified rows · we DO NOT fall back to Places guesses on the
    // strictest-verified surface). When present the BrowseGrid
    // renders a clean "we haven't desk-verified Michelin coverage
    // here yet" message instead of the generic empty-state.
    const [coverageGap, setCoverageGap] = useState(null);
    const [envelope, setEnvelope] = useState(null);
    // Item 21c · founder ear-test 5 Jun 2026 · closed-venue honesty.
    // Surfaced as a banner above results when the top hit's
    // business_status != OPERATIONAL.
    const [closedNotice, setClosedNotice] = useState(null);
    // Item 21g · dish-intent honest banner. We search Places, not
    // menus · NEVER imply we matched a dish when we didn't.
    const [dishIntent, setDishIntent] = useState(null);
    // Founder bug fix 7 Jun 2026 evening · filter honesty banner.
    // When the user explicitly applies a price filter (£/££/£££/££££)
    // we STRICTLY honour it · venues whose price_level is None get
    // dropped, never silently passed. The backend echoes a count
    // (filters_meta.price_filter_dropped_no_data) so we can tell the
    // member exactly WHY some venues are hidden · honest, not silent.
    // Same Asymmetric Trust Rule we apply to Anma's open/closed.
    const [filtersMeta, setFiltersMeta] = useState(null);
    // Mira agent plan envelope · founder direction 9 Feb 2026 ·
    // "world's most sophisticated agentic concierge backed reservation
    // global system". When the search bar receives a conversational
    // query (anything that doesn't route to /browse via the parser)
    // we hit /api/table/agent/plan and surface this envelope in the
    // results view instead of the legacy /search list.
    const [agentPlanEnv, setAgentPlanEnv] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [bookingContext, setBookingContext] = useState(null);
    const [toast, setToast] = useState(null);
    // Filter state · work-order v2 §5
    const [filters, setFilters] = useState({
        cuisine: null, price_max: null, price_min: null,
        rating_min: null, route_in: null, open_now: null, city: null,
    });
    // Sort key · work-order v2 §12 · "relevance" (default) | "rating" |
    // "price_asc" | "price_desc"
    const [sort, setSort] = useState("relevance");
    // Active intent (work-order v2 §14) · null when no chip selected
    const [activeIntent, setActiveIntent] = useState(null);
    const [conciergeEnvelope, setConciergeEnvelope] = useState(null);
    const [tightFormSuggestion, setTightFormSuggestion] = useState(null);
    const [normalisedQuery, setNormalisedQuery] = useState(null);
    const [groups, setGroups] = useState([]);
    // Pagination · work-order v2 §11
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const abortRef = useRef(null);

    const PAGE_SIZE = 24;

    // Forward-ref so runSearch can call runBrowse (declared later
    // in the file). The .current value is assigned via useEffect
    // once runBrowse is in scope.
    const runBrowseRef = useRef(null);

    const runSearch = useCallback(async (raw, opts = {}) => {
        const text = (raw ?? q).trim();
        if (text.length < 2) return;

        // Fresh-query reset (founder catch 10 Feb 2026 · "no filters
        // clear properly"). A free-text search the member typed in the
        // bar must NOT inherit the previous query's tab/city/chip
        // state. The shared `isFreshQueryInvocation` predicate at the
        // top of this file is the single source of truth · runBrowse
        // uses the same gate.
        const isFreshSearch = isFreshQueryInvocation(opts);
        if (isFreshSearch) {
            setActiveTab(null);
            setActiveCity(null);
            setFilters(FILTERS_EMPTY);
            setSort("relevance");
            setActiveIntent(null);
        }

        // ─── Smart query parser (founder direction 9 Feb 2026 ·
        // "the most intelligent filtering system that works") ──────
        // Try the /parse-query endpoint FIRST. If the parser
        // confidently detected a category + (city or another anchor),
        // route to /browse with the filters preselected · gives the
        // member the live-count + graceful-empty chip strip surface
        // that maps cleanly to their intent. Otherwise fall through
        // to the regular Places search.
        if (!opts.skipParse && !opts.filters && !opts.intent) {
            try {
                const intent = await parseTableQuery(text);
                if (
                    intent?.confidence >= 0.65
                    && intent?.target_endpoint === "/api/table/browse"
                    && intent?.target_params?.tab
                ) {
                    const f = {};
                    const tp = intent.target_params;
                    if (tp.city) f.city = tp.city.replace(/\b\w/g, c => c.toUpperCase());
                    if (tp.cuisine) f.cuisine = tp.cuisine;
                    if (tp.price_max) f.price_max = tp.price_max;
                    if (tp.stars) {
                        f.stars = tp.stars.split(",")
                            .map(x => parseInt(x.trim(), 10))
                            .filter(x => [1, 2, 3].includes(x));
                    }
                    if (tp.open_now) f.open_now = true;
                    if (tp.authority) f.authority = tp.authority;
                    setQ(text);
                    return runBrowseRef.current?.(tp.tab, { filters: f });
                }
            } catch {
                // soft-fail · fall through to normal search
            }
        }

        setView("results");
        setQ(text);
        setSubmitted(text);
        setLoading(true);
        setError(null);
        setSelected(null);
        setOffset(0);
        setHasMore(false);
        setConciergeEnvelope(null);
        setTightFormSuggestion(null);
        setNormalisedQuery(null);
        setGroups([]);
        setAgentPlanEnv(null);
        // ── Founder bug fix 18 Feb 2026 · stale-results carryover ──
        // When a fresh search starts we MUST drop any results / meta
        // / interpreted envelope from the prior query (or prior browse
        // tab). Without this, switching from /browse → search shows
        // last-tab's cards under the new count text — a "ghost results"
        // illusion the founder hit in production with
        // "michelin" → "chinese restaurants koramangala". The skeleton
        // grid renders only when `loading && results.length === 0`,
        // so clearing here is what turns the page into honest loading
        // state instead of stale visual lies.
        setResults([]);
        setEnvelope(null);
        setFiltersMeta(null);
        setClosedNotice(null);
        setDishIntent(null);
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        const filterParams = opts.filters !== undefined
            ? opts.filters
            : (isFreshSearch ? FILTERS_EMPTY : filters);
        const sortParam = opts.sort !== undefined
            ? opts.sort
            : (isFreshSearch ? "relevance" : sort);
        // Intent · explicit in opts wins · else clear (re-running a
        // search after the member changes the query drops the active
        // intent chip).
        const intentParam = (opts.intent !== undefined) ? opts.intent : null;
        setActiveIntent(intentParam || null);

        // ─── Mira agent · auto-routing · DISABLED 9 Feb 2026 PM ──
        // Founder regression report 9 Feb 2026 PM: the auto-agent
        // routing introduced in commit b86999a hijacked every
        // free-text query into the stripped MiraPlanView layout. That
        // layout (one card · no refine panel · no photo gallery · no
        // editorial summary · no hours · no similar venues · no
        // link-outs) is far thinner than the production `/search`
        // experience the founder showed via lesconcierge.com/table:
        // a rich browse layout with RestaurantCard + the deep
        // RestaurantDrawer (photo gallery · Google editorial · atmosphere
        // ledger · routing icons · hours · similar venues · what-people-
        // say link-outs · verified contact channels).
        //
        // Fix: stop auto-hijacking. Free-text queries flow through the
        // production-equivalent `/api/table/search` path, render via
        // RestaurantCard + RestaurantDrawer. The agent-plan endpoint
        // remains in the codebase + the /mira surface still uses it
        // explicitly · but the /table search bar does NOT default to it.
        //
        // (If we want to bring agent-plan understanding back to /table
        // later, it has to ENRICH the rich layout · not replace it.)
        const useAgent = false;
        if (useAgent) {
            try {
                const env = await agentPlan({ q: text, signal: ac.signal });
                setAgentPlanEnv(env);
                setResults([]);
                setEnvelope(null);
                setClosedNotice(null);
                setDishIntent(null);
                setFiltersMeta(null);
                window.history.replaceState({}, "", `/table?q=${encodeURIComponent(text)}`);
                setLoading(false);
                return;
            } catch (e) {
                if (e?.name === "AbortError") {
                    setLoading(false);
                    return;
                }
                // Soft-fail · fall through to legacy /search below.
            }
        }

        try {
            const data = await searchTable(text, {
                limit: PAGE_SIZE, offset: 0, signal: ac.signal,
                cuisine: filterParams?.cuisine,
                price_max: filterParams?.price_max,
                price_min: filterParams?.price_min,
                rating_min: filterParams?.rating_min,
                route_in: filterParams?.route_in,
                open_now: filterParams?.open_now || undefined,
                sort: sortParam === "relevance" ? null : sortParam,
                intent: intentParam || undefined,
            });
            if (ac.signal.aborted) return;
            // Concierge-route intent (Item 14 P0 doctrine) — render
            // the hand-off panel instead of a results grid.
            if (data?.concierge_route) {
                setConciergeEnvelope({
                    intent: data.intent,
                    copy: data.concierge_copy,
                });
                setResults([]);
            } else {
                setResults(data.results || []);
                setHasMore(Boolean(data.has_more));
                setOffset((data.results || []).length);
            }
            setEnvelope(data.interpreted || null);
            setNormalisedQuery(data.query_normalised || null);
            setTightFormSuggestion(data.tight_form_suggestion || null);
            setGroups(data.groups || []);
            // Item 21c / 21g · founder doctrine envelopes (5 Jun 2026)
            setClosedNotice(data.closed_notice || null);
            setDishIntent(data.dish_intent || null);
            // 7 Jun 2026 evening · filter honesty banner
            setFiltersMeta(data.filters_meta || null);
            // Remember this search for the home "recently" strip
            // (founder direction · /table audit 29 Jun 2026). Only
            // pushes free-text member searches · we skip pushing for
            // browse / intent / concierge-route handoffs because those
            // didn't originate from the bar.
            if (text && text.length >= 2 && !data?.concierge_route) {
                rememberRecentSearch(text);
                window.dispatchEvent(new Event("lc-recent-update"));
            }
            window.history.replaceState({}, "", `/table?q=${encodeURIComponent(text)}`);
        } catch (e) {
            if (e?.name !== "AbortError" && !ac.signal.aborted) {
                setError(e?.body || e?.message || "Search failed. Please try again.");
                setResults([]);
            }
        } finally {
            if (!ac.signal.aborted) setLoading(false);
        }
    }, [q, filters, sort]);

    const loadMore = useCallback(async () => {
        if (!submitted || loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const data = await searchTable(submitted, {
                limit: PAGE_SIZE, offset,
                cuisine: filters?.cuisine,
                price_max: filters?.price_max,
                price_min: filters?.price_min,
                rating_min: filters?.rating_min,
                route_in: filters?.route_in,
                open_now: filters?.open_now || undefined,
                sort: sort === "relevance" ? null : sort,
                intent: activeIntent || undefined,
            });
            const next = data.results || [];
            setResults((prev) => {
                // dedupe defensively · the backend should already paginate
                // cleanly, but `prev` + slice is safer for the user.
                const seen = new Set(prev.map((v) => v.id));
                return prev.concat(next.filter((v) => !seen.has(v.id)));
            });
            setHasMore(Boolean(data.has_more));
            setOffset(offset + next.length);
        } catch (e) {
            // surface as toast, not block-error · the page already
            // shows the first batch happily.
            setToast({ kind: "error", text: "Could not load more results." });
            setTimeout(() => setToast(null), 4000);
        } finally {
            setLoadingMore(false);
        }
    }, [submitted, offset, hasMore, loadingMore, filters, sort, activeIntent]);

    // Filter handler · re-runs the search with new params · honest
    // narrowing of the live verified pool (no client-side hiding).
    // City changes augment the submitted query so the backend's own
    // city-aware parsing routes correctly (no separate `city` param
    // on /api/table/search · the query is the city signal).
    const handleFilterChange = useCallback((next) => {
        const cityChanged = (next?.city || null) !== (filters?.city || null);
        setFilters(next);
        if (!submitted) return;
        if (cityChanged) {
            // Strip any prior city from the submitted query, then
            // re-add the new one (or just drop it if cleared).
            const seed = (submitted || "").trim();
            const cur = (filters?.city || "").trim();
            let cleaned = seed;
            if (cur) {
                cleaned = seed.replace(
                    new RegExp(`\\b${cur.replace(/[.*+?^${}()|[\\]\\\\]/g, "\\\\$&")}\\b`, "i"),
                    "",
                ).replace(/\s+/g, " ").trim();
            }
            const nextSeed = next?.city
                ? `${cleaned} ${next.city}`.replace(/\s+/g, " ").trim()
                : cleaned;
            setQ(nextSeed);
            runSearch(nextSeed, { filters: next });
        } else {
            runSearch(submitted, { filters: next });
        }
    }, [submitted, runSearch, filters]);

    const handleFilterClear = useCallback(() => {
        const cleared = {
            cuisine: null, price_max: null, price_min: null,
            rating_min: null, route_in: null, open_now: null, city: null,
        };
        setFilters(cleared);
        if (submitted) runSearch(submitted, { filters: cleared });
    }, [submitted, runSearch]);

    const handleSortChange = useCallback((next) => {
        setSort(next);
        if (submitted) runSearch(submitted, { sort: next });
    }, [submitted, runSearch]);

    // ── Top-tab category browse (founder spec 8 Feb 2026) ──────────
    // Tabs are EXCLUSIVE per founder Q1=a · star-holders only show
    // under [Michelin]. URL is the source of truth (?tab=michelin
    // is a sharable hand-off link).
    const runBrowse = useCallback(async (tab, opts = {}) => {
        const tabKey = TAB_KEYS.includes(tab) ? tab : "all";
        // Symmetric fresh-invocation wipe · the same predicate `runSearch`
        // uses. A tab click / city-clear / "Near me" reaches us without
        // explicit opts; an in-browse chip toggle reaches us with
        // `opts.filters`. The free-text-search state pieces (`filters`,
        // `sort`, `activeIntent`, `submitted`, `q`) are not consumed in
        // browse view, but leaving them stale silently leaks back into
        // the next free-text search if the member types one. Closes the
        // symmetric half of BC-004 (founder direction 10 Feb 2026 ·
        // "fix one and leaving its twin is the exact fragmentation we
        // keep killing · one shared util, both protected").
        if (isFreshQueryInvocation(opts)) {
            setFilters(FILTERS_EMPTY);
            setSort("relevance");
            setActiveIntent(null);
        }
        setActiveTab(tabKey);
        const filters = opts.filters || (opts.city ? { city: opts.city } : {});
        setActiveCity(filters.city || null);
        setActiveFilters(filters);
        setView("browse");
        setResults([]);
        setLoading(true);
        setError(null);
        setSelected(null);
        setOffset(0);
        setHasMore(false);
        // ── Founder bug fix 18 Feb 2026 · cross-runner race ──
        // /browse used to fly without an AbortSignal, so a slow
        // michelin /browse could resolve AFTER a subsequent
        // /search and overwrite its results (the production repro
        // showed "24 tables for chinese restaurants koramangala"
        // listing Michelin venues). Both runners now share
        // `abortRef` · whichever call kicks off second wins.
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        try {
            const data = await browseTable({
                tab: tabKey,
                city: filters.city || undefined,
                cuisine: filters.cuisine,
                price_max: filters.price_max,
                stars: filters.stars,
                distinction: filters.distinction,
                open_now: filters.open_now,
                authority: filters.authority,
                ranking_max: filters.ranking_max,
                verification: filters.verification,
                limit: PAGE_SIZE, offset: 0,
                signal: ac.signal,
            });
            if (ac.signal.aborted) return;
            setResults(data.results || []);
            setHasMore(Boolean(data.has_more));
            setOffset((data.results || []).length);
            setCityCounts(data.city_counts || null);
            setBrowseTotal(typeof data.total === "number" ? data.total : null);
            setCoverageGap(data.coverage_gap || null);
            // URL deep-link · every filter survives back/forward
            const params = new URLSearchParams();
            params.set("tab", tabKey);
            if (filters.city) params.set("city", filters.city);
            if (filters.cuisine) params.set("cuisine", filters.cuisine);
            if (filters.price_max) params.set("price_max", filters.price_max);
            if (filters.stars?.length)
                params.set("stars", filters.stars.join(","));
            if (filters.distinction)
                params.set("distinction", filters.distinction);
            if (filters.open_now) params.set("open_now", "1");
            if (filters.authority) params.set("authority", filters.authority);
            if (filters.verification)
                params.set("verification", filters.verification);
            window.history.replaceState({}, "", `/table?${params.toString()}`);
            logTableEvent({
                resolver_output: { destinationType: "category_browse" },
                user_action: "tab_browse",
                context: { tab: tabKey, ...filters },
            });
        } catch (e) {
            if (e?.name === "AbortError" || ac.signal.aborted) return;
            setError(e?.body || e?.message || "Could not load this category.");
            setResults([]);
        } finally {
            if (!ac.signal.aborted) setLoading(false);
        }
    }, []);

    const handleTabChange = useCallback((tabKey, tabMeta) => {
        // Console-only nav tab · Curate routes out to the studio,
        // it's not a category filter (founder direction 11 Feb
        // 2026 PM · "surface /curate as a tab on /table for
        // convenience, gated to concierge/admin").
        if (tabMeta && tabMeta.kind === "nav" && tabMeta.target) {
            window.location.href = tabMeta.target;
            return;
        }
        // Switching tabs clears the filter chip strip · each tab has
        // its own shape (stars only on michelin, authority only on
        // bars/clubs, etc.). City pin is preserved because it makes
        // sense across tabs.
        runBrowse(tabKey, { filters: activeFilters.city ? { city: activeFilters.city } : {} });
    }, [runBrowse, activeFilters.city]);

    // Forward-ref binding · runSearch references runBrowse via ref
    // (it's declared earlier in this function body).
    useEffect(() => {
        runBrowseRef.current = runBrowse;
    }, [runBrowse]);

    // Filter chip strip change · re-runs the browse with the new
    // filter object. The URL is updated by runBrowse so back/forward
    // works · and the link is shareable.
    const handleBrowseFilterChange = useCallback((nextFilters) => {
        runBrowse(activeTab, { filters: nextFilters || {} });
    }, [runBrowse, activeTab]);

    // SearchNearby · founder direction 9 Feb 2026 ·
    // GPS-bias the current tab's intent against /api/table/nearby
    // (Places SearchText with locationBias). DB whispers still
    // overlay on matched place_ids.
    const [nearbyActive, setNearbyActive] = useState(false);
    const runNearby = useCallback(async ({ lat, lng, tab: tabKey }) => {
        setView("browse");
        setActiveTab(tabKey || activeTab || "all");
        setResults([]);
        setLoading(true);
        setError(null);
        setSelected(null);
        setOffset(0);
        setHasMore(false);
        setNearbyActive(true);
        try {
            const data = await searchNearbyTable({
                lat, lng, tab: tabKey || activeTab || "all",
                limit: PAGE_SIZE,
            });
            setResults(data.results || []);
            setHasMore(false);
            setOffset((data.results || []).length);
            logTableEvent({
                resolver_output: { destinationType: "nearby" },
                user_action: "near_me_toggle",
                context: { tab: tabKey, lat, lng },
            });
        } catch (e) {
            setError(e?.body || e?.message || "Couldn't find places near you.");
            setNearbyActive(false);
        } finally {
            setLoading(false);
        }
    }, [activeTab]);

    const handleDestinationCityPick = useCallback((cityName) => {
        runBrowse("destinations", { city: cityName });
    }, [runBrowse]);

    const loadMoreBrowse = useCallback(async () => {
        if (!activeTab || loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const data = await browseTable({
                tab: activeTab,
                city: activeFilters.city || undefined,
                cuisine: activeFilters.cuisine,
                price_max: activeFilters.price_max,
                stars: activeFilters.stars,
                open_now: activeFilters.open_now,
                authority: activeFilters.authority,
                verification: activeFilters.verification,
                limit: PAGE_SIZE, offset,
            });
            const next = data.results || [];
            setResults((prev) => {
                const seen = new Set(prev.map((v) => v.id));
                return prev.concat(next.filter((v) => !seen.has(v.id)));
            });
            setHasMore(Boolean(data.has_more));
            setOffset(offset + next.length);
        } catch {
            setToast({ kind: "error", text: "Could not load more." });
            setTimeout(() => setToast(null), 4000);
        } finally {
            setLoadingMore(false);
        }
    }, [activeTab, activeCity, offset, hasMore, loadingMore]);

    useEffect(() => {
        if (seed) runSearch(seed);
        // Bootstrap from ?tab= deep link (founder spec 8 Feb 2026 ·
        // shareable category links). Only when no ?q seed wins.
        if (!seed && tabSeed && TAB_KEYS.includes(tabSeed)) {
            // Seed full filter object from URL on mount · so
            // ?tab=michelin&city=London&stars=2,3 lands directly on
            // the filtered grid.
            runBrowse(tabSeed, { filters: activeFilters });
        }
        // ── Share-link bootloader (4 Jun 2026 founder doctrine) ───
        // /table?venue={id} → fetch verified detail and open the
        // drawer directly. The friend sees the canonical detail
        // surface, re-resolved live · never our snapshot.
        const venueParam = new URLSearchParams(window.location.search).get("venue");
        if (venueParam) {
            logTableEvent({
                resolver_output: { destinationType: "share_arrival" },
                user_action: "share_link_opened",
                venue: { id: venueParam },
                context: { entry: "share_url" },
            });
            (async () => {
                try {
                    const detail = await getVenueDetail(venueParam);
                    if (detail?.id) {
                        // Render a placeholder venue immediately so
                        // the drawer paints; full enrichment streams in.
                        setSelected(detail);
                    }
                } catch {
                    // honest fall-through: if the share link is broken,
                    // the home page still works.
                }
            })();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Authoritative clean-slate reset · founder direction 10 Feb 2026
    // (BC-007 trap-fix). The brand logo AND every "Clear all" button
    // route here · "take me back to the start" means EVERY piece of
    // session-leak state goes back to its first-load value. Patching
    // one tab and leaving the same trap in five others is the exact
    // fragmentation we keep killing · this is the shared exit.
    const goHome = useCallback(() => {
        setView("home");
        setQ("");
        setSubmitted("");
        setResults([]);
        setEnvelope(null);
        setClosedNotice(null);
        setDishIntent(null);
        setSelected(null);
        setActiveTab(null);
        setActiveCity(null);
        setCityCounts(null);
        setBrowseTotal(null);
        setCoverageGap(null);
        setActiveFilters({});
        setActiveIntent(null);
        setNearbyActive(false);
        setFilters({
            cuisine: null, price_max: null, price_min: null,
            rating_min: null, route_in: null,
        });
        setSort("relevance");
        setFiltersMeta(null);
        setAgentPlanEnv(null);
        setHasMore(false);
        setOffset(0);
        setError(null);
        window.history.replaceState({}, "", "/table");
    }, []);

    const handleSelect = useCallback(async (venue) => {
        // Mark the placeholder as "detail loading" so the drawer can
        // render a "Reading their menu…" research state instead of
        // looking thin while we fetch (work-order v2 §10).
        setSelected({ ...venue, _detail_loading: true });
        logTableEvent({
            resolver_output: { destinationType: "preview_only" },
            user_action: "card_opened_detail",
            venue: {
                name: venue?.name, city: venue?.city,
                evidence_source: venue?.evidence_source,
            },
            context: { query: submitted },
        });
        try {
            const detail = await getVenueDetail(venue.id);
            if (detail && detail.id === venue.id) {
                setSelected((cur) => {
                    if (!cur || cur.id !== venue.id) return cur;
                    return { ...cur, ...detail, _detail_loading: false };
                });
            } else {
                setSelected((cur) => {
                    if (!cur || cur.id !== venue.id) return cur;
                    return { ...cur, _detail_loading: false };
                });
            }
        } catch {
            setSelected((cur) => {
                if (!cur || cur.id !== venue.id) return cur;
                return { ...cur, _detail_loading: false };
            });
        }
    }, [submitted]);

    // Clicking a "similar · also verified" row in the drawer reloads
    // that venue into the same drawer · keeps the discovery journey
    // continuous (work-order v2 §4).
    const handleSelectNearby = useCallback(async (nearby) => {
        if (!nearby?.id) return;
        // Build a placeholder venue out of the nearby card shape so
        // the drawer can render immediately while the full detail
        // payload streams in.
        const placeholder = {
            id: nearby.id,
            name: nearby.name,
            city: nearby.address ? nearby.address.split(",").slice(-3, -2)[0]?.trim() : null,
            country: nearby.address ? nearby.address.split(",").slice(-1)[0]?.trim() : null,
            address: nearby.address,
            rating: nearby.rating,
            user_ratings_total: nearby.user_ratings_total,
            price_level: nearby.price_level,
            place_id: nearby.place_id,
            map_url: nearby.map_url,
            cuisine_tags: nearby.cuisine_tags,
            evidence_source: nearby.evidence_source,
        };
        setSelected({ ...placeholder, _detail_loading: true });
        logTableEvent({
            resolver_output: { destinationType: "preview_only" },
            user_action: "nearby_opened_in_drawer",
            venue: {
                name: nearby?.name,
                evidence_source: nearby?.evidence_source,
            },
            context: { query: submitted, parent: "drawer_nearby" },
        });
        try {
            const detail = await getVenueDetail(nearby.id);
            if (detail && detail.id === nearby.id) {
                setSelected((cur) => {
                    if (!cur || cur.id !== nearby.id) return cur;
                    return { ...cur, ...detail, _detail_loading: false };
                });
            } else {
                setSelected((cur) => {
                    if (!cur || cur.id !== nearby.id) return cur;
                    return { ...cur, _detail_loading: false };
                });
            }
        } catch {
            setSelected((cur) => {
                if (!cur || cur.id !== nearby.id) return cur;
                return { ...cur, _detail_loading: false };
            });
        }
    }, [submitted]);

    const handleReserve = useCallback((venue, routeKey) => {
        // Unified Concierge-wrapped reservation UX (ruling 5 Jun 2026).
        // Every route — instant / direct / concierge — opens the SAME
        // BookingSheet. The sheet posts to /api/table/concierge-request
        // which logs the intent in lc_concierge_basket, fires the
        // W-I1/W-C1 whisper over Gupshup, and (for instant) hands the
        // member the verified provider link on the confirmation step.
        //
        // BC-019 EXCEPTION (founder ear-test 10 Feb 2026 · Jamavar Dubai):
        // For non-iframeable providers (Google Reserve, TableCheck,
        // any *.google.com URL), there is no honest booking surface ·
        // we MUST open the provider URL in a new tab and skip the
        // BookingSheet entirely. NO LC ticket. NO "Request secured".
        // We never fake a confirmation we didn't earn.
        //
        // BC-020 STRUCTURAL FIX (founder ear-test 10 Feb 2026 PM):
        // Previous attempt fired `window.open(url, '_blank')`
        // programmatically. window.open RETURNS null when blocked —
        // never throws — so our catch never fired and the drawer
        // closed silently. The RestaurantDrawer now renders the
        // primary CTA as a real `<a target="_blank">` on external-
        // handoff venues — browsers honour anchor clicks the way
        // they don't honour programmatic open. handleReserve only
        // runs as the anchor's onClick handler (drawer close + log);
        // navigation is the anchor's job, not ours. This eliminates
        // the silent-failure surface for external handoffs.
        const route = routeKey || classifyRoute(venue);
        const providerLink = venue?.resolved_platform?.url;
        const providerName = venue?.resolved_platform?.platform;

        if (isExternalHandoff(providerName, providerLink)) {
            logTableEvent({
                resolver_output: { destinationType: "external_handoff" },
                user_action: "drawer_external_handoff_opened",
                venue: {
                    name: venue?.name, city: venue?.city,
                    evidence_source: venue?.evidence_source,
                },
                context: {
                    query: submitted, route, cta: venue?.booking_cta,
                    provider: providerName, link: providerLink,
                },
            });
            // Universal click-intent · founder ruling 16 Feb 2026 PM-22.
            // The <a target="_blank"> opens the rail natively; this
            // fires Mongo+Zoho FYI in parallel (keepalive, never blocks).
            // See /app/memory/TELEMETRY_DOCTRINE.md
            logTableIntent({
                rail: (providerName || "other").toLowerCase().replace(/\s+/g, "_"),
                entity_id: venue?.id || venue?.place_id || venue?._id || "",
                entity_snapshot: {
                    title: venue?.name,
                    rail: providerName,
                    city: venue?.city,
                    country: venue?.country,
                    category: venue?.category || "restaurant",
                    kind: "restaurant",
                    surface: "table",
                    destination_url: providerLink,
                    provider_name: providerName,
                    image_url: venue?.photo_url || venue?.image_url,
                },
                intent_type: "direct_book_click",
            });
            // MC self-serve-intent (01 Jul 2026): courtesy ping to MC's
            // Zoho when we're inside an MC-tenanted iframe. Fire-and-
            // forget · keepalive · never blocks the tab switch to the
            // venue's booking site. Silently no-ops for non-MC traffic.
            fireSelfServeIntent({
                benefit: "table",
                venue:   venue,
                extra:   { booking_platform: providerName, destination_url: providerLink },
            });
            // The drawer's <a target="_blank"> opens the link itself.
            // We just close the drawer here so the new tab can take
            // focus cleanly. No window.open (would double-open and
            // can be popup-blocked silently).
            setSelected(null);
            return;
        }

        logTableEvent({
            resolver_output: { destinationType: "preview_only" },
            user_action: "drawer_primary_cta_clicked",
            venue: {
                name: venue?.name, city: venue?.city,
                evidence_source: venue?.evidence_source,
            },
            context: { query: submitted, route, cta: venue?.booking_cta },
        });
        setSelected(null);
        setBookingContext({ venue, route });
    }, [submitted]);

    const handleConciergeOverride = useCallback((venue) => {
        // "or have the concierge handle it instead →" — open the same
        // sheet, force route=concierge.
        logTableEvent({
            resolver_output: { destinationType: "concierge_override" },
            user_action: "concierge_override",
            venue: {
                name: venue?.name, city: venue?.city,
                evidence_source: venue?.evidence_source,
            },
            context: { query: submitted },
        });
        setSelected(null);
        setBookingContext({ venue, route: "concierge" });
    }, [submitted]);

    const handleBookingConfirmed = useCallback((result) => {
        // The whisper itself has gone via Gupshup; we just emit the
        // immediate tap-feedback toast. NEVER says "confirmed" on
        // instant — only "Request secured".
        const ref = (result?.member_reference || result?.ticket_id || "logged")
            .toUpperCase();
        logTableEvent({
            resolver_output: bookingContext?.resolverOutput || {},
            user_action: "booking_sheet_submitted",
            venue: {
                name: bookingContext?.venue?.name,
                city: bookingContext?.venue?.city,
                evidence_source: bookingContext?.venue?.evidence_source,
            },
            context: { query: submitted, route: bookingContext?.route },
            concierge_ticket_id: ref,
        });
        setToast({
            kind: "success",
            text: `Request secured · ${ref}`,
        });
        setTimeout(() => setToast(null), 6000);
        // Sheet keeps itself open on its confirmation step · member
        // closes via the Done button.
    }, [bookingContext, submitted]);

    const intent = envelope ? envelopeToIntent(envelope, submitted) : null;
    const placeContext = envelope?.expanded_places?.[0] || null;

    return (
        <div data-table-surface data-testid="table-page" className="t-grain lc-surface-with-float-pill">
            <TableHeader onHome={goHome} />
      <MemberContextBand pillar="table" />

            {/* Top-tab strip · always visible above the content (founder
                spec 8 Feb 2026). Members move between [All]/[Michelin]/
                [Fine Dine]/[Bars]/[Nightclubs]/[Destinations] without
                touching the search field. URL is the source of truth
                (?tab=michelin is a sharable hand-off). */}
            <div
                style={{
                    maxWidth: 1152,
                    marginInline: "auto",
                    paddingInline: 24,
                    paddingTop: 12,
                }}
            >
                <TableTabs value={activeTab} onChange={handleTabChange} showCurate={!isEmbedded()} />
                <div style={{
                    display: "flex", justifyContent: "center",
                    marginTop: 12,
                }}>
                    <NearMeToggle
                        tab={activeTab}
                        active={nearbyActive}
                        onLocated={runNearby}
                    />
                </div>
            </div>

            {view === "browse" && activeTab ? (
                <BrowseSearchBar
                    tab={activeTab}
                    filters={activeFilters}
                    onChange={handleBrowseFilterChange}
                    onClearAll={goHome}
                    onRunSearch={runSearch}
                    filteredTotal={browseTotal}
                />
            ) : null}

            {view === "home" ? (
                <TableHome
                    q={q}
                    setQ={setQ}
                    onRun={runSearch}
                    onAskConcierge={(typed) => setBookingContext({
                        venue: {
                            name: (typed || "").trim() || "Open ask · /table",
                            city: null,
                            country: null,
                            place_id: null,
                            evidence_source: "table-hero-ask-concierge",
                        },
                        route: "concierge",
                    })}
                />
            ) : view === "browse" ? (
                <BrowseGrid
                    tab={activeTab}
                    city={activeCity}
                    cityCounts={cityCounts}
                    coverageGap={coverageGap}
                    results={results}
                    loading={loading}
                    error={error}
                    hasMore={hasMore}
                    loadingMore={loadingMore}
                    onSelect={handleSelect}
                    onLoadMore={loadMoreBrowse}
                    onCityPick={handleDestinationCityPick}
                    onClearCity={() => runBrowse(activeTab)}
                />
            ) : (
                <main
                    data-testid="table-results"
                    className="max-w-6xl mx-auto px-6 pt-8 pb-32"
                    style={{ position: "relative", zIndex: 2 }}
                >
<div className="mb-6">
                        <SearchField
                            value={q}
                            onChange={setQ}
                            onSubmit={runSearch}
                            envelope={envelope}
                            submittedQuery={submitted}
                        />
                    </div>

                    {/* Mira agent · option (c) · "world's most
                       sophisticated agentic concierge backed reservation
                       global system" · founder direction 9 Feb 2026.
                       When the search bar receives a conversational
                       query (no filters/intent/non-default sort), we
                       short-circuit to the agentic plan view: parsed
                       understanding + ranked candidates + per-candidate
                       route_plan (auto_execute deeplink OR Send to
                       Concierge® dispatch). The legacy /search list
                       below still renders when filters are active. */}
                    {agentPlanEnv ? (
                        <MiraPlanView
                            envelope={agentPlanEnv}
                            onSelectCandidate={(c) => handleSelect({
                                id: c.place_id,
                                place_id: c.place_id,
                                name: c.name,
                            })}
                            onDispatched={(r) => setToast({
                                kind: "ok",
                                text: `Sent to Concierge® · #${(r?.dispatch_id || "").slice(0, 6)}`,
                            })}
                            onBackToHome={() => {
                                setView("home");
                                setAgentPlanEnv(null);
                                setQ("");
                                window.history.replaceState({}, "", "/table");
                            }}
                        />
                    ) : null}

                    {/* Legacy /search list renders only when the agent
                       wasn't used (filters/intent path) · doctrine
                       preserves the existing flow for desk operators
                       who refine with explicit filters. */}
                    {!agentPlanEnv ? (
                    <>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                        <div className="flex items-center gap-3 flex-wrap">
                            {intent ? (
                                <span
                                    className="t-mono uppercase"
                                    data-testid="results-intent-chip"
                                    style={{
                                        fontSize: 10,
                                        letterSpacing: "0.14em",
                                        color: "var(--t-brass)",
                                        border: "1px solid var(--t-brass2)",
                                        borderRadius: 20,
                                        padding: "3px 9px",
                                    }}
                                >
                                    {intent.label}
                                </span>
                            ) : null}
                            <p
                                data-testid="results-header"
                                style={{ fontSize: 14, color: "var(--t-ink2)", margin: 0 }}
                            >
                                <span style={{ fontWeight: 600, color: "var(--t-ink)" }}>
                                    {loading ? "…" : results.length}
                                </span>{" "}
                                {results.length === 1 ? "table" : "tables"} for{" "}
                                <span className="t-serif italic" style={{ color: "var(--t-ink)" }}>
                                    “{submitted}”
                                </span>
                                {placeContext ? (
                                    <span style={{ color: "var(--t-ink3)" }}>
                                        {" "}· also matched {placeContext}
                                    </span>
                                ) : null}
                            </p>
                        </div>

                        {/* Sort · work-order v2 §12 · the dropdown
                           was retired 29 Jun 2026 in favour of the
                           visible SortChips row below FilterBar
                           (founder direction · /table human-search
                           overhaul). One sort surface, not two. */}
                    </div>

                    {/* Item 18 · Typo suggestion line — never auto-rewrite, just offer */}
                    {tightFormSuggestion ? (
                        <div
                            data-testid="typo-suggestion-line"
                            style={{
                                fontSize: 13,
                                color: "var(--t-ink2)",
                                marginBottom: 12,
                                lineHeight: 1.5,
                            }}
                        >
                            Showing results for{" "}
                            <span className="t-serif italic" style={{ color: "var(--t-ink)" }}>
                                {normalisedQuery || submitted}
                            </span>{" "}—{" "}
                            <button
                                type="button"
                                data-testid="typo-suggestion-tight-form"
                                onClick={() => runSearch(tightFormSuggestion)}
                                style={{
                                    background: "transparent",
                                    border: 0,
                                    color: "var(--t-brass)",
                                    cursor: "pointer",
                                    fontStyle: "italic",
                                    textDecoration: "underline",
                                    fontFamily: "'Eb Garamond', serif",
                                    fontSize: 13,
                                }}
                            >
                                search instead for {tightFormSuggestion}?
                            </button>
                        </div>
                    ) : null}

                    {/* Item 21c · closed-venue honest banner
                        ("BFlat appears permanently closed — here are
                         verified alternatives nearby"). Founder
                         doctrine 5 Jun 2026: never silently swap. */}
                    {closedNotice ? (
                        <ClosedNoticeBanner notice={closedNotice} />
                    ) : null}

                    {/* Item 21g · dish-intent honest banner
                        ("We search places, not menus yet — here are
                         restaurants in {city}; tell a Concierge® the
                         dish and we'll confirm who serves it.")
                         Founder doctrine 5 Jun 2026: never imply we
                         matched the dish when we didn't. */}
                    {dishIntent ? (
                        <DishIntentBanner
                            dish={dishIntent.dish}
                            city={dishIntent.city}
                        />
                    ) : null}

                    {/* Item 14 · Concierge-route intent envelope */}
                    {conciergeEnvelope ? (
                        <div
                            data-testid="intent-concierge-envelope"
                            style={{
                                background: "var(--t-card)",
                                border: "1px solid var(--t-brass2)",
                                borderRadius: 16,
                                padding: "22px 24px",
                                marginBottom: 20,
                            }}
                        >
                            <div
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 10,
                                    letterSpacing: "0.2em",
                                    color: "var(--t-brass)",
                                    marginBottom: 8,
                                }}
                            >
                                ✦ {conciergeEnvelope.intent?.label}
                            </div>
                            <p
                                className="t-serif"
                                style={{
                                    fontSize: 17, color: "var(--t-ink)",
                                    lineHeight: 1.55, marginBottom: 14,
                                }}
                            >
                                {conciergeEnvelope.copy}
                            </p>
                            <button
                                type="button"
                                data-testid="intent-concierge-cta"
                                onClick={() => handleConciergeOverride({
                                    name: `${conciergeEnvelope.intent?.label} request · ${submitted}`,
                                    city: submitted,
                                    evidence_source: {
                                        kind: "concierge_intent",
                                        id: conciergeEnvelope.intent?.id,
                                        verified_at: new Date().toISOString(),
                                    },
                                })}
                                style={{
                                    background: "var(--t-ink)",
                                    color: "var(--t-paper2)",
                                    border: 0, borderRadius: 12,
                                    padding: "11px 18px",
                                    fontSize: 14, fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                Brief a Concierge® →
                            </button>
                        </div>
                    ) : null}

                    {/* Item 15 · Cuisine groups breadcrumb */}
                    {groups.length > 1 ? (
                        <div
                            className="flex flex-wrap gap-2 mb-4"
                            data-testid="cuisine-groups-row"
                        >
                            <span
                                className="t-mono uppercase"
                                style={{
                                    fontSize: 10,
                                    letterSpacing: "0.18em",
                                    color: "var(--t-ink3)",
                                    alignSelf: "center",
                                }}
                            >
                                grouped by cuisine ·
                            </span>
                            {groups.map((g) => (
                                <span
                                    key={g.cuisine}
                                    data-testid={`cuisine-group-${g.cuisine.toLowerCase()}`}
                                    style={{
                                        fontSize: 12.5,
                                        color: "var(--t-ink)",
                                        background: "var(--t-card)",
                                        border: "1px solid var(--t-line2)",
                                        borderRadius: 14,
                                        padding: "3px 11px",
                                    }}
                                >
                                    {g.label} · {g.count}
                                </span>
                            ))}
                        </div>
                    ) : null}

                    {/* Filter bar · narrows the verified pool via re-query */}
                    {!loading && !error && results.length > 0 ? (
                        <>
                            {/* Concierge® noticed — cross-pillar
                               awareness · two cities? we sketch the
                               hop · anniversary? we set the table ·
                               weekend? /stay has the room. Quiet,
                               opt-in, never hijacks. */}
                            <ConciergeNoticed
                                query={submitted}
                                onMobility={({ from, to }) => {
                                    const url = `/mobility?origin=${encodeURIComponent(from)}&destination=${encodeURIComponent(to)}`;
                                    window.open(url, "_blank", "noopener,noreferrer");
                                }}
                                onStay={({ q }) => {
                                    const url = `/stay?q=${encodeURIComponent(q)}`;
                                    window.open(url, "_blank", "noopener,noreferrer");
                                }}
                                onConcierge={({ occasion }) => {
                                    setToast({ kind: "info", text: `Noted the ${occasion}. The Concierge® will treat it accordingly.` });
                                    setTimeout(() => setToast(null), 4500);
                                }}
                            />
                            {/* Did you mean · only renders when result
                               count is low AND a token in the query is
                               close to a known city/cuisine. */}
                            <DidYouMean
                                query={submitted}
                                resultCount={results.length}
                                onPick={(s) => {
                                    setQ(s);
                                    runSearch(s);
                                }}
                            />
                            {/* Sort chips · resurrects the dead `sort`
                               state · 4-way toggle re-fires the live
                               search through the existing handler. */}
                            <SortChips value={sort} onChange={handleSortChange} />
                            <FilterBar
                            filters={filters}
                            onChange={handleFilterChange}
                            onClear={handleFilterClear}
                            cityCounts={(() => {
                                // Derive a {city: count} dictionary from
                                // the current results so the new City pill
                                // group can render in the FilterBar.
                                // Filter consistency · 29 Jun 2026.
                                const m = {};
                                for (const r of (results || [])) {
                                    const c = (r.city || r.city_name || "").trim();
                                    if (!c) continue;
                                    const pretty = c.replace(/\b\w/g, ch => ch.toUpperCase());
                                    m[pretty] = (m[pretty] || 0) + 1;
                                }
                                return m;
                            })()}
                            onCuisineSearch={(typed) => {
                                // Free-typed cuisine · build a fresh
                                // live query honestly (work-order v2
                                // §5 Item 19 mobile ear-test). We do
                                // NOT filter the existing pool · we
                                // re-query Places with the typed
                                // cuisine prefixed onto the current
                                // submitted text. Places' fuzzy
                                // matcher handles the rest.
                                const clearedFilters = {
                                    cuisine: null, price_max: null, price_min: null,
                                    rating_min: null, route_in: null,
                                };
                                setFilters(clearedFilters);
                                const seed = (submitted || "").trim();
                                // Strip a previously-prefixed cuisine word
                                // if it's still in the query so we don't
                                // accumulate "Turkish Italian London".
                                const nextSeed = seed
                                    ? `${typed} ${seed}`.replace(/\s+/g, " ").trim()
                                    : typed;
                                runSearch(nextSeed, { filters: clearedFilters });
                            }}
                        />
                        </>
                    ) : null}

                    {/* Filter honesty banner · founder doctrine 7 Jun
                       2026 evening · the trust-break fix. When the
                       user explicitly filtered by price AND the
                       backend dropped venues purely because their
                       price_level was None (Google had no data for
                       them), surface a discreet line telling them
                       WHY they're hidden. Never silently exclude
                       unknowns. Same Asymmetric Trust Rule applied
                       at the filter layer. */}
                    {!loading && !error
                        && (filters.price_min !== null || filters.price_max !== null)
                        && filtersMeta?.price_filter_dropped_no_data > 0 ? (
                        <FilterHonestyBanner
                            kind="price"
                            dropped={filtersMeta.price_filter_dropped_no_data}
                            onClear={() => handleFilterChange({
                                ...filters,
                                price_min: null,
                                price_max: null,
                            })}
                            legacyTestid="price-filter-honesty-banner"
                        />
                    ) : null}

                    {/* Cuisine filter honesty banner · same shape (one
                       consolidated component now · see
                       components/table/FilterHonestyBanner.jsx). When
                       a cuisine filter is active AND the backend
                       reports N venues silently dropped because their
                       `cuisine_tags` was empty (Google Places venues),
                       we tell the member. The CUISINE chip in the
                       chip strip stays activated; the banner explains
                       the gap. */}
                    {!loading && !error
                        && filters.cuisine
                        && filtersMeta?.cuisine_filter_dropped_no_data > 0 ? (
                        <FilterHonestyBanner
                            kind="cuisine"
                            dropped={filtersMeta.cuisine_filter_dropped_no_data}
                            onClear={() => handleFilterChange({
                                ...filters,
                                cuisine: null,
                            })}
                            legacyTestid="cuisine-filter-honesty-banner"
                        />
                    ) : null}

                    {loading ? (
                        <SkeletonGrid />
                    ) : error ? (
                        <ErrorBlock message={error} onRetry={() => runSearch(submitted)} />
                    ) : results.length === 0 ? (
                        <EmptyBlock query={submitted} filters={filters} onClear={handleFilterClear} />
                    ) : (
                        <>
                            <div
                                data-testid="table-results-grid"
                                className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
                            >
                                {results.map((v, i) => (
                                    <PillarCard
                                        key={v.id || i}
                                        venue={_tableVenueForPillar(v)}
                                        testidPrefix="table"
                                        onView={() => handleSelect(v)}
                                        onBrief={() => handleSelect(v)}
                                        briefLabel="Send a brief"
                                        reserveUrl={v?.reservation_url || null}
                                        reserveLabel={
                                            v?.booking_platform
                                                ? `Reserve · ${v.booking_platform} ↗`
                                                : "Reserve ↗"
                                        }
                                        benefit="table"
                                        topLeftPill={v?.primary_cuisine ? (
                                            <span style={{
                                                background: "rgba(252,250,244,0.92)", color: "#8B6914",
                                                padding: "3px 9px", borderRadius: 999,
                                                fontFamily: "ui-monospace, monospace",
                                                fontSize: 10, letterSpacing: "0.14em",
                                                fontWeight: 600, textTransform: "uppercase",
                                                border: "1px solid #8B691444",
                                            }}>{v.primary_cuisine}</span>
                                        ) : null}
                                        topRightPill={v?.price_tier ? (
                                            <span style={{
                                                background: "rgba(33,28,21,0.78)", color: "#F4EFE6",
                                                padding: "3px 9px", borderRadius: 999,
                                                fontFamily: "ui-monospace, monospace",
                                                fontSize: 10, letterSpacing: "0.16em", fontWeight: 600,
                                            }}>{v.price_tier}</span>
                                        ) : null}
                                    />
                                ))}
                            </div>

                            {/* Load more · work-order v2 §11 */}
                            {hasMore ? (
                                <div className="flex justify-center mt-8">
                                    <button
                                        type="button"
                                        onClick={loadMore}
                                        disabled={loadingMore}
                                        data-testid="table-load-more"
                                        style={{
                                            background: "var(--t-paper)",
                                            color: "var(--t-ink)",
                                            border: "1px solid var(--t-ink)",
                                            borderRadius: 14,
                                            padding: "13px 28px",
                                            fontSize: 14,
                                            fontWeight: 600,
                                            cursor: loadingMore ? "wait" : "pointer",
                                            letterSpacing: "0.01em",
                                            opacity: loadingMore ? 0.6 : 1,
                                            transition: "all 0.18s",
                                        }}
                                    >
                                        {loadingMore ? "Loading…" : "Load more tables"}
                                    </button>
                                </div>
                            ) : null}
                        </>
                    )}
                    </>
                    ) : null}
                </main>
            )}

            {selected ? (
                <RestaurantDrawer
                    venue={selected}
                    onClose={() => setSelected(null)}
                    onReserve={handleReserve}
                    onConciergeOverride={handleConciergeOverride}
                    onSelectNearby={handleSelectNearby}
                />
            ) : null}

            {bookingContext ? (
                <BookingSheet
                    venue={bookingContext.venue}
                    route={bookingContext.route}
                    onClose={() => setBookingContext(null)}
                    onConfirmed={handleBookingConfirmed}
                />
            ) : null}

            {toast ? (
                <div
                    data-testid="table-toast"
                    className="t-sheet"
                    style={{
                        position: "fixed",
                        bottom: 64,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 70,
                        background: "var(--t-ink)",
                        color: "var(--t-paper2)",
                        padding: "12px 22px",
                        borderRadius: 14,
                        fontSize: 13,
                        fontWeight: 500,
                        letterSpacing: "0.01em",
                        boxShadow: "0 18px 50px rgba(33,28,21,.35)",
                        maxWidth: "min(560px, 92vw)",
                        textAlign: "center",
                    }}
                >
                    {toast.text}
                </div>
            ) : null}

            {/* Main cities rail · sits below the existing curated
                content, above the footer. Click → pre-fills the
                search and runs it (same seed path the URL `?q=`
                uses on mount). */}
            <TopCitiesGrid
              pillar="table"
              picks={PICKS_TABLE}
              onPick={(query) => {
                setQ(query);
                runSearch(query);
                try {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } catch { /* older browsers · ignore */ }
              }}
              eyebrow="Main cities"
              title="Cities we know by table"
            />

            <TableFooter />

            {/* Item 21f · persistent "Talk to a Concierge® on
                WhatsApp" pill · always one tap away (founder ear-
                test 5 Jun 2026). Hidden while a BookingSheet is open
                so it doesn't sit on top of the reservation form, and
                hidden during the home view so the welcome surface
                stays uncluttered. */}
            {view !== "home" && !bookingContext ? (
                <TableWhatsAppPill context="table" />
            ) : null}
        </div>
    );
}

// ── BrowseGrid · category-tab archive surface (founder spec 8 Feb 2026)
//
// Renders a curated grid of human_confirmed venues filtered by the
// active tab. No search field, no filters · the strip is the surface.
// For [Destinations] · the city-chip row at top lets members pin to
// St. Tropez · Monaco · Paris · etc. before drilling in.
const TAB_LABELS = {
    all: "All",
    michelin: "Michelin",
    fine_dine: "Fine Dine",
    bars: "Bars",
    nightclubs: "Nightclubs",
    destinations: "Destinations",
};
const TAB_SUBTITLES = {
    all:          "Every desk-verified table across the world.",
    michelin:     "Active Michelin-starred rooms · verified for 2026.",
    fine_dine:    "Quietly serious cooking · without the star.",
    bars:         "Cocktail rooms worth a detour.",
    nightclubs:   "The door, the lineup, the table — handled.",
    destinations: "Where the city itself is the draw.",
};

function BrowseGrid({
    tab, city, cityCounts, coverageGap, results, loading, error, hasMore, loadingMore,
    onSelect, onLoadMore, onCityPick, onClearCity,
}) {
    return (
        <main
            data-testid="table-browse"
            data-tab={tab}
            className="max-w-6xl mx-auto px-6 pb-32"
            style={{ position: "relative", zIndex: 2 }}
        >
            <div style={{ marginBottom: 18 }}>
                <h1
                    className="t-serif"
                    data-testid="browse-title"
                    style={{
                        fontSize: 32, fontWeight: 400, lineHeight: 1.15,
                        color: "var(--t-ink)", letterSpacing: "-0.01em",
                        margin: 0,
                    }}
                >
                    {TAB_LABELS[tab] || "All"}
                    {city ? (
                        <>
                            <span style={{ color: "var(--t-ink3)" }}>{" · "}</span>
                            <span style={{ color: "var(--t-brass)", fontStyle: "italic" }}>{city}</span>
                        </>
                    ) : null}
                </h1>
                <p
                    style={{
                        fontSize: 13,
                        color: "var(--t-ink2)",
                        marginTop: 6,
                        lineHeight: 1.5,
                    }}
                >
                    {TAB_SUBTITLES[tab] || ""}
                </p>
            </div>

            {/* City-pin chip row · only on [Destinations] tab */}
            {tab === "destinations" && cityCounts && cityCounts.length > 0 ? (
                <div
                    data-testid="destinations-city-row"
                    style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        marginBottom: 24,
                    }}
                >
                    {city ? (
                        <button
                            type="button"
                            data-testid="destinations-clear-city"
                            onClick={onClearCity}
                            style={{
                                background: "transparent",
                                color: "var(--t-ink2)",
                                border: "1px solid var(--t-line2)",
                                borderRadius: 999,
                                padding: "7px 14px",
                                fontSize: 12,
                                cursor: "pointer",
                                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                            }}
                        >
                            ← All destinations
                        </button>
                    ) : null}
                    {cityCounts.map((c) => {
                        const active = city === c.city;
                        return (
                            <button
                                key={c.city}
                                type="button"
                                data-testid={`destinations-city-${c.city.replace(/\s+/g, "-").toLowerCase()}`}
                                onClick={() => onCityPick(c.city)}
                                style={{
                                    background: active ? "var(--t-brass)" : "var(--t-card)",
                                    color: active ? "#fff" : "var(--t-ink)",
                                    border: active
                                        ? "1px solid var(--t-brass)"
                                        : "1px solid var(--t-line2)",
                                    borderRadius: 999,
                                    padding: "8px 16px",
                                    fontSize: 13,
                                    fontWeight: active ? 700 : 500,
                                    cursor: "pointer",
                                    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                                    transition: "all 0.18s",
                                }}
                            >
                                {c.city}
                                <span
                                    style={{
                                        opacity: 0.7,
                                        marginLeft: 6,
                                        fontWeight: 400,
                                    }}
                                >
                                    · {c.count}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : null}

            {loading ? (
                <SkeletonGrid />
            ) : error ? (
                <ErrorBlock message={error} onRetry={() => onCityPick(city)} />
            ) : results.length === 0 ? (
                coverageGap ? (
                    // BC-011 · honest doctrinal hedge · the Michelin tab
                    // never falls back to Google Places guesses · empty
                    // means "the desk hasn't verified coverage here yet",
                    // not "no starred restaurants exist".
                    <div
                        data-testid="browse-coverage-gap"
                        data-tab={coverageGap.tab}
                        data-city={coverageGap.city}
                        style={{
                            background: "var(--t-paper2)",
                            border: "1px solid var(--t-line2)",
                            borderRadius: 18,
                            padding: "48px 32px",
                            textAlign: "center",
                            color: "var(--t-ink2)",
                            maxWidth: 640,
                            margin: "0 auto",
                        }}
                    >
                        <div
                            style={{
                                fontSize: 11,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                color: "var(--t-ink3)",
                                marginBottom: 14,
                            }}
                        >
                            Coverage gap
                        </div>
                        <p
                            data-testid="browse-coverage-gap-message"
                            style={{
                                fontSize: 15,
                                lineHeight: 1.55,
                                color: "var(--t-ink)",
                                margin: "0 auto",
                                maxWidth: 520,
                            }}
                        >
                            {coverageGap.message}
                        </p>
                    </div>
                ) : (
                    <div
                        data-testid="browse-empty"
                        style={{
                            background: "var(--t-paper2)",
                            border: "1px dashed var(--t-line2)",
                            borderRadius: 18,
                            padding: "48px 32px",
                            textAlign: "center",
                            color: "var(--t-ink2)",
                        }}
                    >
                        <p>No verified tables in this category yet.</p>
                    </div>
                )
            ) : (
                <>
                    <div
                        data-testid="browse-grid"
                        className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
                    >
                        {results.map((v, i) => (
                            <PillarCard
                                key={v.id || i}
                                venue={_tableVenueForPillar(v)}
                                testidPrefix="table"
                                onView={() => onSelect(v)}
                                onBrief={() => onSelect(v)}
                                briefLabel="Send a brief"
                                reserveUrl={v?.reservation_url || null}
                                reserveLabel={
                                    v?.booking_platform
                                        ? `Reserve · ${v.booking_platform} ↗`
                                        : "Reserve ↗"
                                }
                                benefit="table"
                                topLeftPill={v?.primary_cuisine ? (
                                    <span style={{
                                        background: "rgba(252,250,244,0.92)", color: "#8B6914",
                                        padding: "3px 9px", borderRadius: 999,
                                        fontFamily: "ui-monospace, monospace",
                                        fontSize: 10, letterSpacing: "0.14em",
                                        fontWeight: 600, textTransform: "uppercase",
                                        border: "1px solid #8B691444",
                                    }}>{v.primary_cuisine}</span>
                                ) : null}
                                topRightPill={v?.price_tier ? (
                                    <span data-testid="table-card-price-tier" style={{
                                        background: "rgba(33,28,21,0.78)", color: "#F4EFE6",
                                        padding: "3px 9px", borderRadius: 999,
                                        fontFamily: "ui-monospace, monospace",
                                        fontSize: 10, letterSpacing: "0.16em", fontWeight: 600,
                                    }}>{v.price_tier}</span>
                                ) : null}
                            />
                        ))}
                    </div>
                    {hasMore ? (
                        <div className="flex justify-center mt-8">
                            <button
                                type="button"
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                data-testid="browse-load-more"
                                style={{
                                    background: "var(--t-paper)",
                                    color: "var(--t-ink)",
                                    border: "1px solid var(--t-ink)",
                                    borderRadius: 14,
                                    padding: "13px 28px",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    cursor: loadingMore ? "wait" : "pointer",
                                    opacity: loadingMore ? 0.6 : 1,
                                }}
                            >
                                {loadingMore ? "Loading…" : "Load more tables"}
                            </button>
                        </div>
                    ) : null}
                </>
            )}
        </main>
    );
}


function SkeletonGrid() {
    // Founder direction 9 Jun 2026 PM late · "while we are making our
    // thoughts come together the places takes some time · instead of
    // showing blank, can we have something like Concierge is putting
    // it together, thinking something that shows it is working and
    // wait a few minutes."
    //
    // Solution: a warm, rotating microcopy line above the skeleton
    // cards. The skeleton cards still ride below for visual rhythm —
    // the new bit is the brand-voice line that tells the member the
    // desk is on it.
    const lines = React.useMemo(() => [
        "Concierge is putting it together…",
        "Reading the room · checking the room…",
        "Pulling verified tables from the archive…",
        "Asking the Concierge® · a few breaths…",
        "Almost there · laying it out for you…",
    ], []);
    const [lineIdx, setLineIdx] = React.useState(0);
    React.useEffect(() => {
        const t = setInterval(
            () => setLineIdx((i) => (i + 1) % lines.length),
            2400,
        );
        return () => clearInterval(t);
    }, [lines.length]);
    return (
        <div data-testid="results-skeleton">
            {/* Warm Concierge voice · cycles every 2.4s so the page
               feels alive even when the network is slow. */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    marginBottom: 18,
                    fontFamily: "var(--t-mono, ui-monospace, monospace)",
                    fontSize: 11,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "var(--t-ink2)",
                }}
                data-testid="results-skeleton-voice"
            >
                <span
                    aria-hidden="true"
                    style={{
                        display: "inline-block",
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: "var(--t-brass)",
                        boxShadow: "0 0 0 4px color-mix(in srgb, var(--t-brass) 18%, transparent)",
                        animation: "t-pulse 1.4s ease-in-out infinite",
                    }}
                />
                <span
                    key={lineIdx}
                    className="t-rise"
                    style={{ animationDuration: "0.6s" }}
                >
                    {lines[lineIdx]}
                </span>
            </div>
            <div
                className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
            >
                {Array.from({ length: 6 }).map((_, i) => (
                    <div
                        key={i}
                        className="t-rise"
                        style={{
                            animationDelay: `${i * 0.05}s`,
                            background: "var(--t-card)",
                            border: "1px solid var(--t-line)",
                            borderRadius: 18,
                            overflow: "hidden",
                            height: 260,
                        }}
                    >
                        <div style={{ height: 120, background: "var(--t-brass-wash)" }} />
                        <div style={{ padding: "18px 20px" }}>
                            <div
                                style={{
                                    height: 18,
                                    background: "var(--t-line)",
                                    borderRadius: 6,
                                    width: "65%",
                                }}
                            />
                            <div
                                style={{
                                    marginTop: 10,
                                    height: 12,
                                    background: "var(--t-line)",
                                    borderRadius: 6,
                                    width: "50%",
                                }}
                            />
                            <div
                                style={{
                                    marginTop: 10,
                                    height: 12,
                                    background: "var(--t-line)",
                                    borderRadius: 6,
                                    width: "80%",
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function EmptyBlock({ query, filters, onClear }) {
    const activeCount = filters ? (
        ((filters.cuisine || "").split(",").filter(Boolean).length) +
        (filters.price_max ? 1 : 0) +
        (filters.rating_min ? 1 : 0) +
        ((filters.route_in || "").split(",").filter(Boolean).length)
    ) : 0;
    return (
        <div
            data-testid="results-empty"
            className="t-rise"
            style={{
                background: "var(--t-paper2)",
                border: "1px dashed var(--t-line2)",
                borderRadius: 18,
                padding: "48px 32px",
                textAlign: "center",
                color: "var(--t-ink2)",
            }}
        >
            <span
                className="t-mono uppercase"
                style={{
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    color: "var(--t-brass)",
                }}
            >
                no tables to show
            </span>
            <h2
                className="t-serif mt-3"
                style={{
                    fontSize: 28,
                    fontWeight: 400,
                    color: "var(--t-ink)",
                    letterSpacing: "-0.01em",
                }}
            >
                We couldn&apos;t find a table for{" "}
                <span style={{ fontStyle: "italic", color: "var(--t-brass)" }}>
                    “{query}”
                </span>
            </h2>
            <p style={{ marginTop: 10, fontSize: 14, maxWidth: 460, marginInline: "auto" }}>
                {activeCount > 0
                    ? "Your filters narrowed the results down to zero. Try clearing one or two refinements."
                    : "Try a more specific name, a city, or a cuisine. Or hand it to a Concierge® — we'll find the room and confirm a table."}
            </p>
            {activeCount > 0 ? (
                <button
                    type="button"
                    onClick={onClear}
                    data-testid="empty-clear-filters"
                    style={{
                        marginTop: 18,
                        background: "var(--t-ink)",
                        color: "var(--t-paper2)",
                        border: 0,
                        borderRadius: 12,
                        padding: "11px 22px",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        letterSpacing: "0.01em",
                    }}
                >
                    Clear all filters
                </button>
            ) : null}
        </div>
    );
}

function ErrorBlock({ message, onRetry }) {
    return (
        <div
            data-testid="results-error"
            className="t-rise"
            style={{
                background: "var(--t-pending-wash)",
                border: "1px solid var(--t-pending)",
                borderRadius: 18,
                padding: "26px 30px",
                color: "var(--t-ink)",
            }}
        >
            <span
                className="t-mono uppercase"
                style={{
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    color: "var(--t-pending)",
                }}
            >
                a hiccup
            </span>
            <p style={{ marginTop: 8, fontSize: 14 }}>{String(message)}</p>
            <button
                onClick={onRetry}
                data-testid="results-error-retry"
                style={{
                    marginTop: 12,
                    background: "var(--t-ink)",
                    color: "var(--t-paper2)",
                    border: 0,
                    borderRadius: 10,
                    padding: "10px 18px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                }}
            >
                Try again
            </button>
        </div>
    );
}


const SORT_OPTIONS = [
    { value: "relevance", label: "Best match" },
    { value: "rating", label: "★ Rating" },
    { value: "price_asc", label: "$ Low → High" },
    { value: "price_desc", label: "$$$$ High → Low" },
];

function SortPicker({ value, onChange }) {
    return (
        <label
            className="flex items-center gap-2"
            data-testid="table-sort-picker"
            style={{ fontSize: 12, color: "var(--t-ink3)" }}
        >
            <span
                className="t-mono uppercase"
                style={{ fontSize: 10, letterSpacing: "0.16em" }}
            >
                Sort
            </span>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid="table-sort-select"
                style={{
                    background: "var(--t-card)",
                    color: "var(--t-ink)",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 10,
                    padding: "6px 10px",
                    fontSize: 13,
                    fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                    cursor: "pointer",
                    outline: "none",
                }}
            >
                {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </label>
    );
}



// ── Item 21f · founder ear-test 5 Jun 2026 · persistent
// "Talk to a Concierge® on WhatsApp" pill. The human is always on
// hand — one tap from any /table view. Rides the existing Gupshup
// number (GUPSHUP_SOURCE_NUMBER), shipped as a `wa.me/<number>`
// deep link so it opens WhatsApp on mobile and web.
const _LC_WHATSAPP_NUMBER = "918792622424";   // GUPSHUP_SOURCE_NUMBER


function waMeLink(prefillText) {
    const t = prefillText
        || "Hi Concierge®, I'd like to talk to a human about a table.";
    return `https://wa.me/${_LC_WHATSAPP_NUMBER}?text=${encodeURIComponent(t)}`;
}


export function TableWhatsAppPill({ context = "table" }) {
    const onClick = () => {
        const here = (typeof window !== "undefined" && window.location?.href) || "";
        const prefill = context === "curate"
            ? `Hi Concierge®, I'm in the studio — quick question.`
            : `Hi Concierge®, I'm browsing ${here} — I'd love a hand.`;
        window.open(waMeLink(prefill), "_blank", "noopener,noreferrer");
    };
    return (
        <a
            href={waMeLink()}
            onClick={(e) => { e.preventDefault(); onClick(); }}
            data-testid="table-whatsapp-pill"
            className="lc-float-pill"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Talk to a Concierge® on WhatsApp"
            style={{
                position: "fixed",
                // Founder ear-test: iOS safe-area + don't sit right
                // under the home indicator
                right: "max(18px, env(safe-area-inset-right, 0px))",
                bottom: "calc(max(18px, env(safe-area-inset-bottom, 0px)) + 8px)",
                zIndex: 60,
                display: "inline-flex", alignItems: "center", gap: 9,
                padding: "12px 18px",
                borderRadius: 999,
                background: "#0a7e3e",      // WhatsApp brand green
                color: "#fff",
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                fontSize: 13, fontWeight: 700, letterSpacing: "0.02em",
                textDecoration: "none",
                boxShadow: "0 12px 32px rgba(33,28,21,0.18)",
                border: "1.5px solid rgba(255,255,255,0.18)",
                minHeight: 44,           // 44px tap-target (Apple HIG)
            }}
        >
            <svg
                width="20" height="20" viewBox="0 0 24 24" fill="none"
                aria-hidden="true"
            >
                <path
                    d="M12 2C6.48 2 2 6.48 2 12c0 1.74.45 3.45 1.3 4.95L2 22l5.2-1.27A9.95 9.95 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm5.5 14.05c-.23.65-1.36 1.24-1.88 1.32-.5.08-1.16.12-1.86-.12-.43-.13-.99-.32-1.7-.63-2.99-1.29-4.93-4.3-5.08-4.5-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.78-.37l.56.01c.18 0 .42-.07.66.5.23.56.79 1.96.86 2.1.07.13.12.29.02.47-.1.18-.15.29-.29.45-.14.16-.3.36-.43.48-.14.13-.3.27-.13.55.17.28.76 1.26 1.65 2.04 1.14 1.01 2.1 1.32 2.38 1.46.28.13.45.11.62-.07.18-.18.71-.84.9-1.13.18-.28.36-.23.62-.13.26.1 1.62.77 1.9.91.28.13.46.2.53.32.07.13.07.71-.16 1.36z"
                    fill="currentColor"
                />
            </svg>
            <span className="lc-float-pill-label">
                Talk to a Concierge<sup
                    style={{
                        fontSize: "0.55em",
                        marginLeft: "1px",
                        fontWeight: "normal",
                        verticalAlign: "super",
                        lineHeight: 0,
                    }}
                    aria-label="registered trademark"
                >®</sup>
            </span>
        </a>
    );
}


// ── Item 21c · closed-venue banner ────────────────────────────────
//
// Founder doctrine 5 Jun 2026: "Read Places business_status and say
// it honestly · then show alternatives. Don't silently swap."

function ClosedNoticeBanner({ notice }) {
    if (!notice?.name) return null;
    const verb = notice.status === "CLOSED_TEMPORARILY"
        ? "appears temporarily closed"
        : "appears permanently closed";
    return (
        <div
            data-testid="table-closed-notice"
            style={{
                background: "var(--t-card)",
                border: "1px solid var(--t-line)",
                borderLeft: "3px solid var(--t-brass)",
                borderRadius: 14,
                padding: "16px 18px",
                marginBottom: 18,
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 10, letterSpacing: "0.2em",
                    color: "var(--t-brass)", marginBottom: 6,
                }}
            >
                · note from Concierge®
            </div>
            <p
                className="t-serif"
                style={{
                    fontSize: 16, color: "var(--t-ink)",
                    lineHeight: 1.45, margin: 0,
                }}
            >
                <strong>{notice.name}</strong> {verb}
                {notice.city ? ` in ${notice.city}` : ""} — here are verified
                alternatives nearby.
            </p>
        </div>
    );
}


// ── Item 21g · dish-intent banner ─────────────────────────────────
//
// Founder doctrine 5 Jun 2026: "we search places, not menus yet —
// tell a Concierge® the dish and we'll confirm." NEVER imply we
// matched the dish when we didn't.

function DishIntentBanner({ dish, city }) {
    // Founder ear-test 5 Jun 2026 night · the dish-query observability
    // rail. Every banner-shown is unmet demand · log it. Every click-
    // through is unmet-demand-they-cared-enough-to-ask-about · log it.
    // The two together tell us which dishes to prioritise for real
    // menu-search (Item 21g roadmap) and which cities the gap is widest
    // in (Item 18 curated-CSV cue).
    //
    // iter24 fix: React.StrictMode runs effects twice in dev to surface
    // bugs · we de-dupe with a ref so the ping fires exactly once per
    // (dish, city) view, matching the production behaviour.
    const lastPinged = useRef("");
    useEffect(() => {
        const key = `${dish}::${city || ""}`;
        if (lastPinged.current === key) return;
        lastPinged.current = key;
        pingDishQuery({ dish, city, source: "banner_shown" });
    }, [dish, city]);

    // Anchor matches the pill (founder iter23 a11y nit + iter24 parity):
    // middle-click, right-click "open in new tab" and screen readers
    // all work uniformly across every Concierge® CTA.
    const prefill = `Hi Concierge®, I'm looking for a place that serves ${dish}` +
        (city ? ` in ${city}` : "") +
        ". Can you confirm who actually serves it?";
    const href = waMeLink(prefill);
    return (
        <div
            data-testid="table-dish-intent-notice"
            style={{
                background: "var(--t-card)",
                border: "1px solid var(--t-brass2)",
                borderRadius: 14,
                padding: "16px 18px",
                marginBottom: 18,
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 10, letterSpacing: "0.2em",
                    color: "var(--t-brass)", marginBottom: 6,
                }}
            >
                · dish search, honest note
            </div>
            <p
                className="t-serif"
                style={{
                    fontSize: 16, color: "var(--t-ink)",
                    lineHeight: 1.45, margin: 0,
                }}
            >
                We search places, not menus — yet. Here are restaurants
                {city ? ` in ${city}` : ""}; tell a <BrandConcierge />
                {" "}you&apos;re after <em>{dish}</em> and we&apos;ll confirm who
                actually serves it.
            </p>
            <div style={{ marginTop: 10 }}>
                <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="table-dish-intent-talk-cta"
                    onClick={() => {
                        // Fire the click-through ping BEFORE the navigation
                        // so sendBeacon lands · doesn't block the open.
                        pingDishQuery({
                            dish, city, source: "wa_clickthrough",
                        });
                    }}
                    style={{
                        display: "inline-flex", alignItems: "center",
                        background: "var(--t-brass)",
                        color: "#fff",
                        border: 0, borderRadius: 999,
                        padding: "8px 16px",
                        fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                        fontSize: 12, fontWeight: 700,
                        letterSpacing: "0.02em",
                        textDecoration: "none",
                        cursor: "pointer",
                        minHeight: 36,
                    }}
                >
                    Tell a Concierge® the dish →
                </a>
            </div>
        </div>
    );
}
